/**
 * Quizkosh — Exam Capability Registry (Phase 1, architecture only)
 *
 * Declarative registry that future generation/rendering layers consult to
 * decide which advanced features are available for a given exam code.
 *
 * IMPORTANT (Phase 1):
 *   • Only NEET_UG declares `examIntelligenceMode: true`.
 *   • Every other exam keeps its current behavior — nothing is activated.
 *   • Adding an exam here does NOT change any existing UI by itself.
 *
 * Source modes are also declared here (single source of truth) so the
 * normalization service and the UI both agree on the same set.
 */

// ─── Source Modes ─────────────────────────────────────────────────────────
// New mode `exam_intel` is added at the architecture level. UI surfaces it
// only when the selected exam's capability flag `examIntelligenceMode` is true.
export const SOURCE_MODES = Object.freeze({
  TOPIC: "topic",
  PASTE: "paste",
  FILE: "file",
  IMAGE: "image",
  EXAM_INTEL: "exam_intel",
});

// ─── Exam Codes ──────────────────────────────────────────────────────────
// Stable identifiers. UI labels can change; codes stay forever.
export const EXAM_CODES = Object.freeze({
  NEET_UG: "NEET_UG",
  JEE: "JEE",
  UPSC: "UPSC",
  SSC: "SSC",
  CAT: "CAT",
  BANK_PO: "BANK_PO",
  GOVERNMENT_EXAMS: "GOVERNMENT_EXAMS",
  GRE: "GRE",
  GMAT: "GMAT",
  SAT: "SAT",
  IELTS: "IELTS",
  TOEFL: "TOEFL",
  PMP: "PMP",
  EA: "EA",
  TECHNICAL_CERTIFICATIONS: "TECHNICAL_CERTIFICATIONS",
  GENERAL_KNOWLEDGE: "GENERAL_KNOWLEDGE",
  PRIMARY: "PRIMARY",
  MIDDLE_SCHOOL: "MIDDLE_SCHOOL",
  HIGH_SCHOOL: "HIGH_SCHOOL",
  COLLEGE: "COLLEGE",
});

// Map current human-readable UI labels (already used in CATEGORIES) → exam code.
// Lookup is forgiving (case/whitespace insensitive).
const LABEL_TO_CODE = {
  "neet": EXAM_CODES.NEET_UG,
  "jee": EXAM_CODES.JEE,
  "upsc": EXAM_CODES.UPSC,
  "ssc": EXAM_CODES.SSC,
  "cat": EXAM_CODES.CAT,
  "bank exams": EXAM_CODES.BANK_PO,
  "government exams": EXAM_CODES.GOVERNMENT_EXAMS,
  "gre": EXAM_CODES.GRE,
  "gmat": EXAM_CODES.GMAT,
  "sat": EXAM_CODES.SAT,
  "ielts": EXAM_CODES.IELTS,
  "toefl": EXAM_CODES.TOEFL,
  "pmp": EXAM_CODES.PMP,
  "ea": EXAM_CODES.EA,
  "technical certifications": EXAM_CODES.TECHNICAL_CERTIFICATIONS,
  "general knowledge": EXAM_CODES.GENERAL_KNOWLEDGE,
  "primary": EXAM_CODES.PRIMARY,
  "middle school": EXAM_CODES.MIDDLE_SCHOOL,
  "high school": EXAM_CODES.HIGH_SCHOOL,
  "college": EXAM_CODES.COLLEGE,
};

export function examCodeForLabel(label) {
  if (!label) return null;
  return LABEL_TO_CODE[String(label).trim().toLowerCase()] || null;
}

// ─── Default capability vector ────────────────────────────────────────────
// Every exam starts here. Advanced exams override individual flags.
const DEFAULT_CAPS = Object.freeze({
  examIntelligenceMode: false,   // tile visible on source page
  supportsLatex: false,          // LaTeX in question/options
  supportsImages: false,         // generation may include image URL
  supportsDiagrams: false,       // structured diagram blueprint
  supportsSvg: false,            // inline SVG code
  supportsScientificNotation: false,
  supportsAssertionReason: false,
  supportsMatching: false,
  supportsRichText: false,       // HTML question body
  supportsPassages: false,       // RC-style shared stem
  supportsCharts: false,         // data tables/charts in stem
  rendererKey: "default",        // which renderer to mount (Phase 2)
});

// ─── Registry ─────────────────────────────────────────────────────────────
export const EXAM_REGISTRY = Object.freeze({
  [EXAM_CODES.NEET_UG]: {
    examCode: EXAM_CODES.NEET_UG,
    examName: "National Eligibility cum Entrance Test (UG)",
    category: "Medical Entrance",
    rendererKey: "neet_v2",
    examIntelligenceMode: true,
    supportsLatex: true,
    supportsImages: true,
    supportsDiagrams: true,
    supportsSvg: true,
    supportsScientificNotation: true,
    supportsAssertionReason: true,
    supportsMatching: true,
    supportsRichText: true,
    supportsPassages: false,
    supportsCharts: false,
  },
});

/** Returns the full capability object for an exam code (or default vector). */
export function getCapabilities(examCode) {
  if (!examCode) return { ...DEFAULT_CAPS, examCode: null };
  const registered = EXAM_REGISTRY[examCode];
  return { ...DEFAULT_CAPS, ...(registered || {}), examCode };
}

/** Tiny convenience: is this exam wired for the advanced pipeline yet? */
export function isAdvancedExam(examCode) {
  return !!(examCode && EXAM_REGISTRY[examCode]);
}

/** Read-only snapshot for /api/exam/capabilities-style consumers. */
export function snapshotRegistry() {
  const out = {};
  for (const code of Object.keys(EXAM_REGISTRY)) {
    out[code] = { ...DEFAULT_CAPS, ...EXAM_REGISTRY[code] };
  }
  return out;
}
